import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { AppDispatch } from '../store/store';
import { fetchInvoices } from '../store/slices/invoiceSlice';

interface UseOptimizedDataFetchingProps {
  page: number;
  pageSize: number;
  statusFilter: string;
  businessUnitFilter: string;
  searchTerm: string;
  dateFrom: Date | null;
  dateTo: Date | null;
}

// Debounce utility for Safari performance optimization
const useDebounce = (value: any, delay: number) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

export const useOptimizedDataFetching = ({
  page,
  pageSize,
  statusFilter,
  businessUnitFilter,
  searchTerm,
  dateFrom,
  dateTo,
}: UseOptimizedDataFetchingProps) => {
  const dispatch = useDispatch<AppDispatch>();
  const [isLoading, setIsLoading] = useState(false);
  const abortControllerRef = useRef<AbortController | null>(null);

  // Debounce search term for better Safari performance
  const debouncedSearchTerm = useDebounce(searchTerm, 300);
  
  // Debounce other filters for better performance
  const debouncedStatusFilter = useDebounce(statusFilter, 200);
  const debouncedBusinessUnitFilter = useDebounce(businessUnitFilter, 200);

  // Optimized fetch function with abort controller
  const fetchData = useCallback(async () => {
    // Cancel previous request if still pending
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Create new abort controller
    abortControllerRef.current = new AbortController();

    try {
      setIsLoading(true);
      
      const params: any = {
        page,
        limit: pageSize,
        status: debouncedStatusFilter !== 'all' ? debouncedStatusFilter : undefined,
        businessUnitId: debouncedBusinessUnitFilter !== 'all' ? debouncedBusinessUnitFilter : undefined,
        search: debouncedSearchTerm || undefined
      };
      
      // Add date filters - with validation
      if (dateFrom && dateFrom instanceof Date && !isNaN(dateFrom.getTime())) {
        params.dateFrom = dateFrom.toISOString().split('T')[0];
      }
      if (dateTo && dateTo instanceof Date && !isNaN(dateTo.getTime())) {
        params.dateTo = dateTo.toISOString().split('T')[0];
      }
      
      await dispatch(fetchInvoices(params)).unwrap();
      
    } catch (error: any) {
      // Ignore abort errors
      if (error.name !== 'AbortError') {
        console.error('Fetch error:', error);
      }
    } finally {
      setIsLoading(false);
      abortControllerRef.current = null;
    }
  }, [
    dispatch,
    page,
    pageSize,
    debouncedStatusFilter,
    debouncedBusinessUnitFilter,
    debouncedSearchTerm,
    dateFrom,
    dateTo
  ]);

  // Effect to fetch data when dependencies change
  useEffect(() => {
    fetchData();
    
    // Cleanup on unmount
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [fetchData]);

  // Manual refresh function
  const refreshData = useCallback(() => {
    fetchData();
  }, [fetchData]);

  return {
    isLoading,
    refreshData,
  };
};
